'use client';

import { motion } from 'framer-motion';
import CounterAnimation from '@/components/ui/CounterAnimation';
import { MapPin, Mail, Phone, Calendar, Code2, Palette, Rocket, Zap } from 'lucide-react';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const features = [
  { icon: Code2, title: 'Clean Code', desc: 'Well-structured, maintainable code' },
  { icon: Palette, title: 'Modern Design', desc: 'Pixel-perfect, beautiful UI/UX' },
  { icon: Rocket, title: 'Performance', desc: 'Fast, optimized applications' },
  { icon: Zap, title: 'Responsive', desc: 'Works on all devices smoothly' },
];

type DynamicStat = {
  slug: string;
  label: string;
  icon: string;
  value: string;
  suffix: string;
  order: number;
};

type AboutData = {
  headline: string | null;
  bio1: string | null;
  bio2: string | null;
  imageUrl: string | null;
  imagePublicId: string | null;
  location: string | null;
  email: string | null;
  phone: string | null;
  availability: string | null;
  yearsExperience: number | null;
  projectsCount: number | null;
  clientsCount: number | null;
  reposCount: number | null;
  skills: string[];
};

function splitHeadline(headline: string): { prefix: string; highlight: string } {
  if (!headline) return { prefix: '', highlight: '' };
  const idx = headline.toLowerCase().lastIndexOf('people love');
  if (idx === -1) {
    const parts = headline.trim().split(' ');
    if (parts.length < 2) return { prefix: '', highlight: headline };
    const highlight = parts.slice(-2).join(' ');
    const prefix = parts.slice(0, -2).join(' ') + ' ';
    return { prefix, highlight };
  }
  return {
    prefix: headline.slice(0, idx),
    highlight: headline.slice(idx),
  };
}

export default function About() {
  const data = useSiteData<AboutData | null>('about', '/api/about', null);
  const dynamicStats = useSiteData<DynamicStat[]>('stats', '/api/stats', []);

  const headline = data?.headline ?? '';
  const bio1 = data?.bio1 ?? '';
  const bio2 = data?.bio2 ?? '';
  const location = data?.location ?? '';
  const email = data?.email ?? '';
  const phone = data?.phone ?? '';
  const availability = data?.availability ?? '';
  const skills = data?.skills ?? [];
  // yearsExperience and projectsCount are still used by the floating About badges;
  // clientsCount and reposCount are now fully owned by /api/stats (StatCounter table).
  const yearsExperience = data?.yearsExperience ?? 0;
  const projectsCount = data?.projectsCount ?? 0;
  const imageUrl = data?.imageUrl ?? null;

  const { prefix, highlight } = splitHeadline(headline);

  return (
    <section id="about" className="about-section">
      <div className="section-container">
        <motion.div
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <span className="section-label">About Me</span>
          <h2 className="section-title">Get to know the person behind the code</h2>
          <p className="section-subtitle">
            A passionate Full-Stack Developer based in {location}
          </p>
        </motion.div>

        <div className="about-grid">
          {/* Left: Image */}
          <motion.div
            className="about-visual"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="about-visual-wrapper">
              <div className="about-image-bg" />
              <div className={`about-image ${imageUrl ? '' : 'about-image--mono'}`}>
                {imageUrl ? (
                  <OptimizedImage
                    src={imageUrl}
                    alt="About"
                    className="about-image-img"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="about-image-text">NAN</span>
                )}
              </div>

              <div className="about-badge about-badge-top">
                <div className="about-badge-value">{yearsExperience}+</div>
                <div className="about-badge-label">Years<br />Experience</div>
              </div>

              <div className="about-badge about-badge-bottom">
                <div className="about-badge-value">{projectsCount}+</div>
                <div className="about-badge-label">Projects<br />Delivered</div>
              </div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            className="about-content"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <h3 className="about-headline">
              {prefix}
              <span className="gradient-text">{highlight}</span>
            </h3>

            <p className="about-text">{bio1}</p>
            <p className="about-text">{bio2}</p>

            <div className="about-info-grid">
              <div className="about-info-item">
                <MapPin size={18} className="about-info-icon" />
                <div>
                  <div className="about-info-label">Location</div>
                  <div className="about-info-value">{location}</div>
                </div>
              </div>
              <div className="about-info-item">
                <Mail size={18} className="about-info-icon" />
                <div>
                  <div className="about-info-label">Email</div>
                  <div className="about-info-value">{email}</div>
                </div>
              </div>
              <div className="about-info-item">
                <Phone size={18} className="about-info-icon" />
                <div>
                  <div className="about-info-label">Phone</div>
                  <div className="about-info-value">{phone}</div>
                </div>
              </div>
              <div className="about-info-item">
                <Calendar size={18} className="about-info-icon" />
                <div>
                  <div className="about-info-label">Status</div>
                  <div className="about-info-value about-info-available">
                    <span className="about-dot" /> {availability}
                  </div>
                </div>
              </div>
            </div>

            <div className="about-skill-tags">
              {skills.map((skill, i) => (
                <motion.span
                  key={`${skill}-${i}`}
                  className="about-skill-tag"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.03, duration: 0.3 }}
                >
                  {skill}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Stats — single source: /api/stats (active-only). Render nothing if empty. */}
        {dynamicStats && dynamicStats.length > 0 && (
          <motion.div
            className="about-stats"
            style={{
              gridTemplateColumns: `repeat(${Math.min(dynamicStats.length, 4)}, 1fr)`,
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={fadeUp}
          >
            {dynamicStats.map((s) => {
              const parsed = parseInt(s.value.replace(/[^0-9]/g, ''), 10);
              const target = Number.isFinite(parsed) ? parsed : 0;
              return (
                <div key={s.slug} className="about-stat-card">
                  <CounterAnimation
                    target={target}
                    suffix={s.suffix || ''}
                    className="about-stat-number"
                  />
                  <div className="about-stat-label">{s.label}</div>
                </div>
              );
            })}
          </motion.div>
        )}

        {/* Features */}
        <div className="about-features">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="about-feature"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <div className="about-feature-icon">
                  <Icon size={22} />
                </div>
                <h4 className="about-feature-title">{feature.title}</h4>
                <p className="about-feature-desc">{feature.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
